import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface Project {
  id: number;
  url: string;
  domain: string | null;
  title: string | null;
  status: string | null;
  framework: string | null;
  last_analyzed_at: string | null;
  created_at: string;
  tenant_id: number | string | null;
  tenant_name: string | null;
  tenant_tier: string | null;
  tenant_status: string | null;
  owner_name: string | null;
  owner_email: string | null;
}

type SortKey = 'url' | 'title' | 'status' | 'tenant_name' | 'last_analyzed_at' | 'created_at';

interface SortConfig {
  key: SortKey;
  direction: 'asc' | 'desc';
}

const ITEMS_PER_PAGE = 25;

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'url', label: 'Project' },
  { key: 'tenant_name', label: 'Tenant' },
  { key: 'status', label: 'Status' },
  { key: 'last_analyzed_at', label: 'Last Analyzed' },
  { key: 'created_at', label: 'Created' }
];

const PAGER_BUTTON =
  'px-3 py-2 text-sm font-medium rounded-xl border border-slate-600/50 bg-slate-700/50 ' +
  'text-slate-300 hover:bg-slate-600/50 hover:text-white disabled:opacity-50 ' +
  'disabled:cursor-not-allowed transition-all duration-200';

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

const ProjectsManagement: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchProjects();
  }, [searchQuery, currentPage, sortConfig]);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
        sort: sortConfig.key,
        direction: sortConfig.direction
      });
      if (searchQuery) params.append('search', searchQuery);

      const response = await apiFetch(`/api/admin/projects?${params}`);
      if (!response.ok) throw new Error('Failed to fetch projects');

      const data = await response.json();
      setProjects(data.projects || []);
      setTotalItems(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const applySearch = (query: string) => {
    setSearchInput(query);
    setSearchQuery(query.trim());
    setCurrentPage(1);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const firstItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const lastItem = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Projects</h2>
          <p className="text-sm text-slate-400 mt-1">
            {totalItems.toLocaleString()} project{totalItems !== 1 ? 's' : ''}
            {searchQuery ? ` matching "${searchQuery}"` : ' across all tenants'}
          </p>
        </div>
        <button
          onClick={fetchProjects}
          disabled={isLoading}
          className="bg-slate-700/50 hover:bg-slate-600/50 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center space-x-2 transition-all duration-200"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          applySearch(searchInput);
        }}
        className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-4 flex items-center space-x-3"
      >
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by domain, project title, tenant name, or user email..."
            className="w-full bg-slate-700/50 border border-slate-600/50 rounded-xl pl-10 pr-4 py-2.5 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
          />
        </div>
        <button
          type="submit"
          className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-6 py-2.5 rounded-xl text-sm font-medium shadow-lg transition-all duration-200"
        >
          Search
        </button>
        {searchQuery && (
          <button
            type="button"
            onClick={() => applySearch('')}
            className="bg-slate-700/50 hover:bg-slate-600/50 text-white px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
          >
            Clear
          </button>
        )}
      </form>

      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50 border-b border-slate-600">
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors whitespace-nowrap"
                  >
                    {column.label}
                    {sortConfig.key === column.key && (
                      <span className="ml-1 text-indigo-400">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {isLoading ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-6 py-12 text-center text-slate-400">
                    Loading projects...
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-6 py-12 text-center text-slate-400">
                    No projects found
                  </td>
                </tr>
              ) : (
                projects.map((project) => (
                  <tr key={project.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
                      >
                        {project.domain || project.url}
                      </a>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {project.title || 'Untitled'} · ID {project.id}
                        {project.framework ? ` · ${project.framework}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">
                        {project.tenant_name || 'Unknown tenant'}
                        <span className="ml-2 text-xs font-normal text-slate-400">
                          #{project.tenant_id ?? '—'}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {project.owner_email || 'No users'}
                        {project.tenant_tier ? ` · ${project.tenant_tier}` : ''}
                        {project.tenant_status ? ` · ${project.tenant_status}` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300 capitalize">
                      {project.status || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                      {formatDate(project.last_analyzed_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">
                      {formatDate(project.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && totalPages > 1 && (
          <div className="bg-slate-700/30 border-t border-slate-700 px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-slate-400">
              Showing {firstItem} to {lastItem} of {totalItems} projects
            </div>
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={PAGER_BUTTON}
              >
                Previous
              </button>
              <span className="text-sm text-slate-300 px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={PAGER_BUTTON}
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsManagement;
